import { type FC, useState } from "react";
import { Link } from "react-router-dom";
import { PageTitle } from "../../../components/PageTitle";
import {
  type AdminFeatureFlag,
  useAdminFeatureFlagsApi,
  useAdminTenantsApi,
  useUpdateAdminFeatureFlagApi,
} from "../../../hooks/useAdminApi";

/** カテゴリ badge の色マップ */
const CATEGORY_COLORS: Record<string, string> = {
  matching: "badge-primary",
  delivery: "badge-secondary",
  email: "badge-accent",
  system: "badge-warning",
};

/**
 * Feature Flag 管理ページ
 * @return {JSX.Element} Feature Flag 管理ページ
 */
export const FeatureFlagsPage: FC = () => {
  const { data, loading, error, refetch } = useAdminFeatureFlagsApi();
  const { data: tenantData } = useAdminTenantsApi();
  const { updateFlag, setTenantOverride, deleteTenantOverride } =
    useUpdateAdminFeatureFlagApi();

  const [actionLoading, setActionLoading] = useState(false);
  const [expandedFlag, setExpandedFlag] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [addOverrideFlag, setAddOverrideFlag] = useState<string | null>(null);

  // カテゴリ一覧を取得
  const categories = [
    ...new Set(data?.items?.map(f => f.category) || []),
  ].sort();

  // フィルタリング
  const filteredFlags =
    data?.items?.filter(
      f => categoryFilter === "all" || f.category === categoryFilter,
    ) || [];

  /**
   * グローバル enabled トグル
   * @param {AdminFeatureFlag} flag 対象フラグ
   */
  const handleToggleEnabled = async (flag: AdminFeatureFlag) => {
    setActionLoading(true);
    try {
      await updateFlag(flag.id, !flag.enabled);
      await refetch();
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * テナント override を設定
   * @param {string} flagId フラグID
   * @param {string} tenantId テナントID
   * @param {boolean} enabled 有効/無効
   */
  const handleSetOverride = async (
    flagId: string,
    tenantId: string,
    enabled: boolean,
  ) => {
    setActionLoading(true);
    try {
      await setTenantOverride(flagId, tenantId, enabled);
      await refetch();
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * テナント override を削除
   * @param {string} flagId フラグID
   * @param {string} tenantId テナントID
   */
  const handleDeleteOverride = async (flagId: string, tenantId: string) => {
    setActionLoading(true);
    try {
      await deleteTenantOverride(flagId, tenantId);
      await refetch();
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * テナント override を新規追加
   * @param {string} flagId フラグID
   * @param {string} tenantId テナントID
   */
  const handleAddOverride = async (flagId: string, tenantId: string) => {
    setAddOverrideFlag(null);
    await handleSetOverride(flagId, tenantId, true);
  };

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>フィーチャーフラグ管理</PageTitle>
        <div className="alert alert-error">
          <svg
            className="stroke-current shrink-0 h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>エラーが発生しました: {error.message}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <PageTitle>フィーチャーフラグ管理</PageTitle>
        <Link to="/admin" className="btn btn-ghost btn-sm">
          &larr; ダッシュボードに戻る
        </Link>
      </div>

      {/* カテゴリフィルター */}
      <div className="mb-6 flex gap-2 flex-wrap">
        <button
          onClick={() => setCategoryFilter("all")}
          className={`btn btn-sm ${categoryFilter === "all" ? "btn-primary" : "btn-ghost"}`}
        >
          All
        </button>
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setCategoryFilter(cat)}
            className={`btn btn-sm ${categoryFilter === cat ? "btn-primary" : "btn-ghost"}`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* フラグ一覧テーブル */}
      <div className="card bg-base-100 shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>キー</th>
                <th>名前</th>
                <th>カテゴリ</th>
                <th>グローバル</th>
                <th>Override</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center">
                    <span className="loading loading-spinner loading-md"></span>
                  </td>
                </tr>
              ) : filteredFlags.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-gray-500">
                    Feature Flag が見つかりません
                  </td>
                </tr>
              ) : (
                filteredFlags.map(flag => (
                  <FlagRow
                    key={flag.id}
                    flag={flag}
                    expanded={expandedFlag === flag.id}
                    actionLoading={actionLoading}
                    tenants={tenantData?.items || []}
                    addOverrideOpen={addOverrideFlag === flag.id}
                    onToggle={() => handleToggleEnabled(flag)}
                    onExpand={() =>
                      setExpandedFlag(expandedFlag === flag.id ? null : flag.id)
                    }
                    onSetOverride={(tenantId, enabled) =>
                      handleSetOverride(flag.id, tenantId, enabled)
                    }
                    onDeleteOverride={tenantId =>
                      handleDeleteOverride(flag.id, tenantId)
                    }
                    onAddOverrideOpen={() =>
                      setAddOverrideFlag(
                        addOverrideFlag === flag.id ? null : flag.id,
                      )
                    }
                    onAddOverride={tenantId =>
                      handleAddOverride(flag.id, tenantId)
                    }
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 統計 */}
      <div className="mt-4 text-sm text-gray-600">
        全 {data?.total || 0} 件中 {filteredFlags.length} 件を表示
      </div>
    </div>
  );
};

// =====================================
// FlagRow component
// =====================================

type FlagRowProps = {
  flag: AdminFeatureFlag;
  expanded: boolean;
  actionLoading: boolean;
  tenants: { id: string; name: string }[];
  addOverrideOpen: boolean;
  onToggle: () => void;
  onExpand: () => void;
  onSetOverride: (tenantId: string, enabled: boolean) => void;
  onDeleteOverride: (tenantId: string) => void;
  onAddOverrideOpen: () => void;
  onAddOverride: (tenantId: string) => void;
};

/**
 * フラグ行コンポーネント
 * @param {FlagRowProps} props プロパティ
 * @return {JSX.Element} フラグ行
 */
const FlagRow: FC<FlagRowProps> = ({
  flag,
  expanded,
  actionLoading,
  tenants,
  addOverrideOpen,
  onToggle,
  onExpand,
  onSetOverride,
  onDeleteOverride,
  onAddOverrideOpen,
  onAddOverride,
}) => {
  const overrideTenantIds = new Set(flag.overrides.map(o => o.tenantId));
  const availableTenants = tenants.filter(t => !overrideTenantIds.has(t.id));

  return (
    <>
      <tr className="hover">
        <td>
          <div>
            <code className="text-sm font-mono">{flag.key}</code>
            {flag.description && (
              <p className="text-xs text-gray-400 mt-1">{flag.description}</p>
            )}
          </div>
        </td>
        <td className="font-medium">{flag.name}</td>
        <td>
          <span
            className={`badge badge-sm ${CATEGORY_COLORS[flag.category] || "badge-ghost"}`}
          >
            {flag.category}
          </span>
        </td>
        <td>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              className="toggle toggle-primary toggle-sm"
              checked={flag.enabled}
              disabled={actionLoading}
              onChange={onToggle}
            />
            <span
              className={`badge badge-sm ${flag.enabled ? "badge-success" : "badge-error"}`}
            >
              {flag.enabled ? "ON" : "OFF"}
            </span>
          </div>
        </td>
        <td>
          {flag.overrides.length > 0 && (
            <span className="badge badge-ghost badge-sm">
              {flag.overrides.length}件
            </span>
          )}
        </td>
        <td>
          <button onClick={onExpand} className="btn btn-ghost btn-xs">
            {expanded ? "閉じる" : "詳細"}
          </button>
        </td>
      </tr>

      {/* 展開パネル: テナント override */}
      {expanded && (
        <tr>
          <td colSpan={6} className="bg-base-200/50 px-8 py-4">
            <div className="text-sm font-semibold mb-3">テナント Override</div>

            {flag.overrides.length === 0 ? (
              <p className="text-sm text-gray-500 mb-3">
                テナント個別設定はありません
              </p>
            ) : (
              <div className="space-y-2 mb-3">
                {flag.overrides.map(override => (
                  <div
                    key={override.id}
                    className="flex items-center justify-between bg-base-100 rounded-lg px-4 py-2"
                  >
                    <span className="text-sm">{override.tenantName}</span>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        className="toggle toggle-primary toggle-sm"
                        checked={override.enabled}
                        disabled={actionLoading}
                        onChange={() =>
                          onSetOverride(override.tenantId, !override.enabled)
                        }
                      />
                      <span
                        className={`badge badge-sm ${override.enabled ? "badge-success" : "badge-error"}`}
                      >
                        {override.enabled ? "ON" : "OFF"}
                      </span>
                      <button
                        onClick={() => onDeleteOverride(override.tenantId)}
                        disabled={actionLoading}
                        className="btn btn-ghost btn-xs text-error"
                      >
                        削除
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* テナント追加 */}
            {availableTenants.length > 0 && (
              <div className="relative inline-block">
                <button
                  onClick={onAddOverrideOpen}
                  className="btn btn-outline btn-xs"
                >
                  + テナント追加
                </button>
                {addOverrideOpen && (
                  <div className="absolute z-10 mt-1 bg-base-100 border rounded-lg shadow-lg py-1 min-w-[200px]">
                    {availableTenants.map(tenant => (
                      <button
                        key={tenant.id}
                        onClick={() => onAddOverride(tenant.id)}
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-base-200"
                      >
                        {tenant.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

export default FeatureFlagsPage;
